import cx from 'clsx';
import { isGranted } from 'common/helpers.ts';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { IDelete, IDownload, IError } from 'ui/component/Icons.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Tooltip } from 'ui/component/Tooltip.tsx';
import { DeleteDialog } from '#admin/component/DeleteDialog.tsx';
import { DocumentForbidden } from '#admin/component/DocumentForbidden.tsx';
import { getTranslatedError } from '#admin/helpers.ts';
import { useDeleteDocument } from '#admin/hook/useDeleteDocument.ts';
import { useDocumentDownloadUrl } from '#admin/hook/useDocumentDownloadUrl.ts';
import { useFetchDocuments } from '#admin/hook/useFetchDocuments.ts';
import { useMe } from '#admin/hook/useMe.tsx';

type DocumentActionsProps = {
  documentUrl: string;
  documentId: string;
  onDeleteSuccess?: () => void;
  disableDownload?: boolean;
  // Disable delete functionality (e.g., when logistics_ex_id is present which means the document was not uploaded through Atlas)
  disableDelete?: boolean;
};

export const DocumentActions: React.FC<DocumentActionsProps> = ({ documentUrl, documentId, onDeleteSuccess, disableDownload = false, disableDelete = false, ...otherProps }) => {
  const { t } = useTranslation();
  const { me } = useMe();
  const documentUrls = React.useMemo(() => [documentUrl], [documentUrl]);
  const { results, isFetching } = useFetchDocuments(documentUrls);
  const { info, error: fetchDocumentError } = results[0] || {};
  const { fetchDocument, errorMessage, isDownloading } = useDocumentDownloadUrl(info, t);
  const { deleteDocument, isDeleting, error: deleteError } = useDeleteDocument(documentId);
  const [isDeleteOpen, setIsDeleteOpen] = React.useState(false);

  // Check if user has permission to delete this document
  const canDelete = !disableDelete && isGranted(me, 'DocumentDelete', documentId);

  const handleDownload = () => void fetchDocument(true);

  const handleDeleteConfirm = async () => {
    setIsDeleteOpen(false);
    try {
      await deleteDocument();
      onDeleteSuccess?.();
    } catch (error) {
      console.error('Failed to delete document:', error);
    }
  };

  if (errorMessage || fetchDocumentError?.message) {
    return <DocumentForbidden />;
  }

  if (isFetching) {
    return (
      <div className="relative size-11 border border-grey-border px-3 py-2">
        <Spinner size={20} centered={true} />
      </div>
    );
  }

  return (
    <>
      <DeleteDialog
        isOpen={isDeleteOpen}
        onClose={() => setIsDeleteOpen(false)}
        onConfirm={handleDeleteConfirm}
        title={t('dialogs.documentDelete.title')}
        message={t('dialogs.documentDelete.confirmMessage')}
      />
      <div
        className={cx('relative border px-3 py-2 transition-all', {
          'border-grey-border': !isDownloading && !errorMessage && !deleteError,
          'border-red': !isDownloading && (errorMessage || deleteError),
        })}
        {...otherProps}
      >
        <div className="flex flex-row items-center gap-2">
          <span className={cx((errorMessage || deleteError) && 'text-red')}>{info?.filename || 'Document'}</span>

          {!disableDownload && !isDownloading && !errorMessage && (
            <IDownload className="ml-auto fill-grey-label hover:cursor-pointer hover:fill-blue" size={24} onClick={handleDownload} />
          )}

          {canDelete && !isDeleting && !deleteError && <IDelete className="fill-grey-label hover:cursor-pointer hover:fill-red" size={24} onClick={() => setIsDeleteOpen(true)} />}

          {/* Error states */}
          {!isDownloading && !isDeleting && (errorMessage || deleteError) && (
            <Tooltip text={errorMessage || getTranslatedError(deleteError, t) || 'An error occurred'}>
              <IError className="fill-red" size={24} />
            </Tooltip>
          )}

          {/* Loading state */}
          {isDownloading && <Spinner size={20} />}
          {isDeleting && <Spinner size={20} />}
        </div>
      </div>
    </>
  );
};
